<template>
  <div class="modal fade show plan-modal" aria-labelledby="productionPlanDetailTitle">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content plan-modal-content">
        <div class="plan-modal-header">
          <div class="d-flex align-items-center gap-1 flex-wrap">
            <div>
              <h4 class="mb-0">{{ planTitle }}</h4>
              <small class="text-muted">{{ plan.plan_number }}</small>
            </div>
            <span class="status-pill ms-1" :style="statusStyle">{{ statusLabel }}</span>
            <span v-if="plan.over_produced_qty > 0" class="badge bg-warning text-dark">
              Over produced {{ plan.over_produced_qty }}
            </span>
          </div>
          <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
        </div>

        <div class="modal-body plan-modal-body">
          <div class="progress-section">
            <div class="row g-1 mb-1">
              <div class="col-6 col-lg-3">
                <div class="qty-card">
                  <div class="qty-label">Planned</div>
                  <div class="qty-value">{{ plan.qty }}</div>
                </div>
              </div>
              <div class="col-6 col-lg-3">
                <div class="qty-card">
                  <div class="qty-label">Produced</div>
                  <div class="qty-value text-primary">{{ plan.produced_qty }}</div>
                </div>
              </div>
              <div class="col-6 col-lg-3">
                <div class="qty-card">
                  <div class="qty-label">Remaining</div>
                  <div class="qty-value text-success">{{ plan.remaining_qty }}</div>
                </div>
              </div>
              <div class="col-6 col-lg-3">
                <div class="qty-card">
                  <div class="qty-label">Over Produced</div>
                  <div class="qty-value text-warning">{{ plan.over_produced_qty }}</div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">Progress</small>
              <small class="fw-bold text-primary">{{ progressLabel }}%</small>
            </div>
            <div class="progress" style="height: 6px;">
              <div class="progress-bar" role="progressbar" :style="`width: ${progressPercent}%`"></div>
            </div>
          </div>

          <div class="detail-section">
            <div class="section-title">What to produce</div>
            <div class="row g-1">
              <div class="col-12 col-lg-4">
                <div class="detail-label">Standard Costing</div>
                <div class="detail-value">{{ displayValue(plan.product_costing_name) }}</div>
              </div>
              <div class="col-12 col-lg-4">
                <div class="detail-label">Product/Item</div>
                <div class="detail-value">{{ displayValue(plan.product_name) }}</div>
              </div>
              <div class="col-12 col-lg-4">
                <div class="detail-label">Target Quantity</div>
                <div class="detail-value">{{ displayValue(plan.qty) }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <div class="section-title">Schedule &amp; resources</div>
            <div class="row g-1">
              <div class="col-12 col-lg-3">
                <div class="detail-label">From Date &amp; Time</div>
                <div class="detail-value">{{ formatDateTime(plan.from_date_time) }}</div>
              </div>
              <div class="col-12 col-lg-3">
                <div class="detail-label">To Date &amp; Time</div>
                <div class="detail-value">{{ formatDateTime(plan.to_date_time) }}</div>
              </div>
              <div class="col-12 col-lg-3">
                <div class="detail-label">Machine/Line</div>
                <div class="detail-value">{{ displayValue(plan.machine_name) }}</div>
              </div>
              <div class="col-12 col-lg-3">
                <div class="detail-label">In-charge Employee</div>
                <div class="detail-value">{{ displayValue(plan.incharge_name) }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <div class="section-title">Demand</div>
            <div class="row g-1">
              <div class="col-12 col-lg-6">
                <div class="detail-label">Order</div>
                <div class="detail-value">{{ displayValue(plan.order_number) }}</div>
              </div>
              <div class="col-12 col-lg-6">
                <div class="detail-label">Order Item</div>
                <div class="detail-value">{{ displayValue(plan.line_item_product_name) }}</div>
              </div>
              <div class="col-12 col-lg-6">
                <div class="detail-label">Customer</div>
                <div class="detail-value">{{ displayValue(plan.customer_name) }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <div class="section-title">Notes &amp; attachments</div>
            <div class="row g-1">
              <div class="col-12">
                <div class="detail-label">Remarks</div>
                <div class="detail-value">{{ displayValue(plan.remarks) }}</div>
              </div>
              <div v-if="hasAttachments" class="col-12 attachment-wrap">
                <div class="detail-label mb-1">Attachments</div>
                <BlobFileViewer :attachments="plan.attachments" :delete-permission="false"/>
              </div>
            </div>
          </div>
        </div>

        <div class="plan-modal-footer">
          <small v-if="lockedNote" class="text-muted me-auto locked-note">{{ lockedNote }}</small>
          <button @click="closeModal" class="btn btn-outline-secondary">Close</button>
          <button
              v-if="plan.can_cancel"
              :disabled="loading"
              @click="onCancelPlan"
              class="btn btn-outline-danger"
          >Cancel Plan
          </button>
          <button v-if="canEdit" @click="onEdit" class="btn btn-primary">Edit</button>
          <button v-if="canAddProduction" @click="onAddProduction" class="btn btn-success">Add Production</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import BlobFileViewer from '@/components/atom/BlobFileViewer'
import handlePurchase from '@/services/modules/purchase'
import { getProductionPlanStatusColor } from '@/services/utils/global'
import useDate from '@/services/utils/day'

const props = defineProps({
  plan: {
    type: Object,
    default: () => ({})
  },
  redirectQuery: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['onEdit', 'onChanged'])

const route = useRoute()
const router = useRouter()
const store = useStore()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const { updateProductionPlanStatus } = handlePurchase()
const { formatDate } = useDate()

const loading = ref(false)

const planTitle = computed(() => {
  if (!props.plan) return ''
  if (props.plan.title) return props.plan.title
  return props.plan.product_name
})

const statusStyle = computed(() => {
  const color = getProductionPlanStatusColor(props.plan.status)
  return `background:${color.bg};color:${color.text};border:1px solid ${color.border}`
})

const statusLabel = computed(() => {
  if (!props.plan.status) return ''
  return props.plan.status.replace('_', ' ')
})

const progressPercent = computed(() => {
  if (!props.plan.qty) return 0
  const percent = (props.plan.produced_qty / props.plan.qty) * 100
  if (percent > 100) return 100
  return percent
})

const progressLabel = computed(() => {
  return Math.round(progressPercent.value)
})

const canEdit = computed(() => {
  return props.plan.can_edit === true
})

const canAddProduction = computed(() => {
  return props.plan.can_add_production === true
})

const lockedNote = computed(() => {
  if (props.plan.status === 'cancelled') return 'This plan is cancelled, so it can no longer be edited or produced.'
  if (props.plan.status === 'on_hold') return 'This plan is on hold. Edit it and set the status back to draft to add a production.'
  if (!canEdit.value) return 'Production has been added against this plan, so it can no longer be edited.'
  return ''
})

const hasAttachments = computed(() => {
  if (!props.plan.attachments) return false
  return props.plan.attachments.length > 0
})

function displayValue(value) {
  if (!value) return '-'
  return value
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value.replace(' ', 'T'))
  if (isNaN(date.getTime())) return '-'
  return formatDate(date)
}

function closeModal() {
  store.state.isModalOpenTwo = false
}

function onEdit() {
  emit('onEdit', props.plan)
}

function getProductionQuery() {
  if (!props.redirectQuery) return { production_plan_id: props.plan.id, type: 'production-plan' }
  return Object.assign({ production_plan_id: props.plan.id }, props.redirectQuery)
}

function onAddProduction() {
  router.push({
    name: 'production-add',
    params: route.params,
    query: getProductionQuery()
  })
}

async function onCancelPlan() {
  const confirmed = window.confirm('Cancel this production plan? This removes it from active planning.')
  if (!confirmed) return

  loading.value = true
  const payload = {
    company_id: route.params.companyId,
    status: 'cancelled'
  }

  await updateProductionPlanStatus(props.plan.id, payload).then((res) => {
    if (!res.status) return showError(res.message)
    closeModal()
    emit('onChanged')
    return showSuccess(res.message)
  }).catch(() => {
    showError('Unable to cancel the production plan.')
  }).finally(() => {
    loading.value = false
  })
}
</script>

<style scoped>
.plan-modal {
  display: block;
  background: #1c1d1c66;
}

.plan-modal .modal-dialog {
  max-width: 960px;
  width: calc(100% - 2rem);
  margin-left: auto;
  margin-right: auto;
}

.plan-modal-content {
  border: 0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
}

.plan-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.85rem;
  border-bottom: 1px solid #eef0f4;
  background: #fbfcfd;
}

.plan-modal-header h4 {
  font-size: 1rem;
}

.plan-modal-body {
  padding: 0.6rem 0.85rem;
  max-height: calc(100vh - 170px);
  overflow-y: auto;
}

.plan-modal-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  border-top: 1px solid #eef0f4;
  background: #fbfcfd;
}

.locked-note {
  font-size: 0.7rem;
  max-width: 45%;
}

.progress-section {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  padding: 0.5rem 0.6rem;
  margin-bottom: 0.5rem;
  background: #fbfcfd;
}

.detail-section {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  padding: 0.4rem 0.6rem 0.55rem;
  margin-bottom: 0.5rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 0.62rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.3rem;
}

.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: capitalize;
}

.qty-card {
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 6px;
  padding: 5px 8px;
  text-align: center;
}

.qty-label {
  font-size: 0.62rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.qty-value {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.3;
}

.detail-label {
  font-size: 0.62rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.detail-value {
  font-size: 0.85rem;
  color: #1f2937;
  font-weight: 500;
  word-break: break-word;
}

.attachment-wrap :deep(.container-fluid) {
  padding: 0;
}

.attachment-wrap :deep(.container-fluid > .row) {
  margin: 0;
  gap: 0.35rem;
}

.attachment-wrap :deep(.container-fluid > .row > [class*="col-"]) {
  flex: 0 1 auto;
  width: auto;
  max-width: 260px;
  padding: 0;
}

.attachment-wrap :deep(.files) {
  border: 1px solid #e5e7eb;
  background-color: #fbfcfd;
  border-radius: 6px;
  padding: 6px 8px;
  margin: 0;
  flex-wrap: nowrap;
  align-items: center;
}

.attachment-wrap :deep(.files > .col-2) {
  flex: 0 0 auto;
  width: auto;
  padding-right: 8px;
}

.attachment-wrap :deep(.files > .col-10) {
  flex: 1 1 auto;
  width: auto;
  padding: 0;
}

.attachment-wrap :deep(.files .fa-2x) {
  font-size: 1.1em;
  color: #6b7280;
}

.attachment-wrap :deep(.files h5) {
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.3;
  word-break: break-word;
}
</style>
